import React, { useState } from 'react';
import { PlayIcon, VideoIcon, Volume2Icon, MaximizeIcon } from 'lucide-react';
interface VideoReplayProps {
  /** URL de Vimeo, Wistia, YouTube o archivo propio. */
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  title?: string;
  /** Texto superpuesto sobre la miniatura, editable desde el constructor. */
  overlayTitle?: React.ReactNode;
  className?: string;
  /** Ancho máximo del reproductor. */
  maxWidthClass?: string;
  /**
   * true solo dentro del constructor: muestra el aviso de que falta la URL.
   * En la landing pública nunca aparece ningún mensaje interno.
   */
  editing?: boolean;
}
function toEmbedUrl(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}
function isFileUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Reproductor de vídeo reutilizable en proporción 16:9, protagonista del hero.
 * Se usa en la landing de AVA y en cualquier landing con bloque de vídeo.
 */
export function VideoReplay({
  url,
  thumbnailUrl,
  caption,
  title = 'Vídeo replay',
  overlayTitle,
  className = '',
  maxWidthClass = 'max-w-5xl',
  editing = false
}: VideoReplayProps) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = url ? toEmbedUrl(url) : null;
  const hasSource = Boolean(url);
  return <figure className={`w-full ${maxWidthClass} mx-auto ${className}`} data-id="element-675" data-mp="2vovstl35wmp5">
      <div className="ava-video-shell relative w-full overflow-hidden rounded-2xl bg-navy ring-1 ring-black/5 sm:rounded-3xl" data-id="element-676" data-mp="30nyqulkq5snm">
        <div className="relative aspect-video w-full" data-id="element-677" data-mp="11c621c83btlz">
          {playing && hasSource ? embedUrl ? <iframe src={embedUrl} title={title} className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen data-id="element-678" data-mp="169eagl3fsja1" /> : isFileUrl(url as string) ? <video src={url} controls autoPlay className="absolute inset-0 h-full w-full object-cover" data-id="element-679" data-mp="24n1ztel2krmp" /> : <iframe src={url} title={title} className="absolute inset-0 h-full w-full" allowFullScreen data-id="element-680" data-mp="2imuluwlnijoc" /> : <button type="button" onClick={() => hasSource && setPlaying(true)} aria-label={hasSource ? 'Reproducir el vídeo' : 'Vídeo pendiente de configurar'} className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40" data-id="element-681" data-mp="2dmf843xgja5u">
              {thumbnailUrl ? <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" data-id="element-682" data-mp="31cj5htk1ydoe" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface text-muted-foreground" data-id="element-683" data-mp="2xaxukdr2w9gu">
                  <VideoIcon className="h-8 w-8" aria-hidden="true" data-id="element-684" data-mp="3bbibt5hchx88" />
                  <span className="text-sm" data-id="element-685" data-mp="36bm9rbs7kfvb">Miniatura pendiente</span>
                </span>}

              <span className="absolute inset-0 bg-gradient-to-t from-black-premium/35 via-transparent to-transparent" data-id="element-686" data-mp="3px5wqsvdh5s5" />

              {/* Texto sobre la miniatura: anclado arriba y con ancho máximo
                  para no solaparse nunca con el botón de reproducción. */}
              {overlayTitle && <span className="absolute inset-x-0 top-3 mx-auto max-w-[88%] whitespace-pre-line px-4 text-center text-[15px] font-bold leading-[1.2] text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] sm:top-[14%] sm:max-w-none sm:px-6 sm:text-3xl" data-id="element-687" data-mp="3ky2t9cv1j7ma">
                  {overlayTitle}
                </span>}

              {/* Botón de reproducción */}
              <span className="absolute left-1/2 top-1/2 flex h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out group-hover:scale-105 sm:h-24 sm:w-24" data-id="element-688" data-mp="2x9t9eghe0txs">
                <span className="absolute inset-0 rounded-full ring-8 ring-white/25" data-id="element-689" data-mp="2s83zj45wimba" />
                <PlayIcon className="ml-1 h-6 w-6 text-primary sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true" data-id="element-690" data-mp="2l55tm66yd9dl" />
              </span>

              {/* Barra de controles decorativa */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3 text-white/90 sm:px-6 sm:py-4" data-id="element-691" data-mp="2bfmb51n5jhj9">
                <PlayIcon className="h-4 w-4 flex-none" fill="currentColor" aria-hidden="true" data-id="element-692" data-mp="26grti30x7z0q" />
                <span className="text-[11px] tabular-nums sm:text-xs" data-id="element-693" data-mp="22ghfc32z62as">0:00 / 4:37</span>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-694" data-mp="1xfl7jefi5w03">
                  <span className="block h-full w-[4%] rounded-full bg-white" data-id="element-695" data-mp="1sgqivvuutd82" />
                </span>
                <Volume2Icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-696" data-mp="1nj8dugjyji5c" />
                <MaximizeIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-697" data-mp="3mw2u34xx0gav" />
              </span>

              {/* Aviso solo visible dentro del constructor. */}
              {!hasSource && editing && <span className="absolute inset-x-4 bottom-12 mx-auto w-fit rounded-full bg-white/90 px-3 py-1 text-center text-xs font-medium text-muted-foreground" data-id="element-698" data-mp="3qdqhmnbwsqdl">
                  Añade la URL del vídeo desde el editor
                </span>}
            </button>}
        </div>
      </div>

      {caption && <figcaption className="mx-auto mt-8 max-w-2xl text-center text-base text-muted-foreground sm:text-lg" data-id="element-699" data-mp="7z7ujbv4tgvl">
          {caption}
        </figcaption>}
    </figure>;
}